import { getNotifications, markAsReadAllNotifications } from 'apis'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { NotificationDTO } from 'types'

interface NotificationProps {
    notification: NotificationDTO
    onNavigate: (link: string) => void
}

const getDate = (date: string | number | Date) => {
    const today = new Date();
    const postDateTime = new Date(date);

    const diffMilliseconds = today.getTime() - postDateTime.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));
    const diffHours = Math.floor(diffMilliseconds / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) return `${diffMinutes}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays >= 1) return `${diffDays}일 전`;

    if (
        postDateTime.getDate() === today.getDate() &&
        postDateTime.getMonth() === today.getMonth() &&
        postDateTime.getFullYear() === today.getFullYear()
    ) {
        const hour = postDateTime.getHours().toString().padStart(2, "0");
        const minute = postDateTime.getMinutes().toString().padStart(2, "0");
        return `${hour}:${minute}`;
    } else {
        const month = (postDateTime.getMonth() + 1).toString().padStart(2, "0");
        const day = postDateTime.getDate().toString().padStart(2, "0");
        return `${month}.${day}`;
    }
};

const getNotificationStyle = (type: string) => {
    switch (type) {
        case 'PAYMENT': return { icon: '💳', bg: 'bg-emerald-50', iconColor: 'text-emerald-600' };
        case 'SETTLEMENT': return { icon: '💰', bg: 'bg-amber-50', iconColor: 'text-amber-600' };
        case 'ALERT': return { icon: '📩', bg: 'bg-blue-50', iconColor: 'text-blue-600' };
        case 'EVENT': return { icon: '🎉', bg: 'bg-purple-50', iconColor: 'text-purple-600' };
        case 'NOTICE': return { icon: '📢', bg: 'bg-sky-50', iconColor: 'text-sky-600' };
        default: return { icon: '🔔', bg: 'bg-achromatic-50', iconColor: 'text-achromatic-600' };
    }
};

const Notification = ({ notification, onNavigate }: NotificationProps) => {
    const style = getNotificationStyle(notification.type);
    const hasLink = !!notification.link;

    const handleClick = () => {
        if (hasLink) {
            onNavigate(notification.link);
        }
    };

    return (
        <div
            className={`flex items-start gap-3.5 py-4 px-5 border-b border-achromatic-50 last:border-b-0 transition-colors
                ${hasLink ? 'cursor-pointer hover:bg-primary-50/30 active:bg-primary-50/50' : 'hover:bg-achromatic-50/50'}`}
            onClick={handleClick}
        >
            <div className={`flex-shrink-0 w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center`}>
                <span className="text-lg leading-none">{style.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-[13px] font-bold text-achromatic-900 line-clamp-1">{notification.title}</h3>
                    <span className="text-[11px] text-achromatic-300 flex-shrink-0 pt-0.5">
                        {notification.createdAt ? getDate(notification.createdAt) : ''}
                    </span>
                </div>
                <p className="text-[12px] text-achromatic-400 mt-0.5 line-clamp-2 leading-relaxed">{notification.content}</p>
                {hasLink && (
                    <span className="text-[11px] text-primary-500 font-semibold mt-1.5 inline-flex items-center gap-0.5">
                        자세히 보기
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </span>
                )}
            </div>
        </div>
    )
}

const NotificationComponent = () => {
    const navigate = useNavigate();
    const [notificationList, setNotificationList] = useState<NotificationDTO[]>([])

    const { data, isLoading } = useQuery('notificationList', getNotifications, {
        onSuccess: (data) => {
            setNotificationList(data.data.data)
        },
    });

    const { mutate } = useMutation(markAsReadAllNotifications);

    useEffect(() => {
        mutate();
    }, []);

    const handleNavigate = (link: string) => {
        navigate(link);
    };

    return (
        <div className="animate-fadeUp">
            <h1 className="text-lg font-bold text-achromatic-900 mb-4">알림</h1>

            <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none">
                {isLoading && (
                    <div className="flex items-center justify-center py-16">
                        <div className="w-5 h-5 border-2 border-achromatic-200 border-t-primary-500 rounded-full animate-spin" />
                    </div>
                )}

                {!isLoading && notificationList && notificationList.length > 0 ? (
                    <div className="flex flex-col">
                        {notificationList.map((notification) => (
                            <Notification
                                key={notification.id}
                                notification={notification}
                                onNavigate={handleNavigate}
                            />
                        ))}
                    </div>
                ) : !isLoading && (
                    <div className="flex flex-col items-center justify-center py-20 gap-2">
                        <span className="text-4xl">🔔</span>
                        <p className="text-sm font-semibold text-achromatic-800 mt-2">알림이 없습니다</p>
                        <p className="text-xs text-achromatic-400">새 알림이 도착하면 여기에 표시됩니다</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default NotificationComponent